import { useEffect } from "react";
import type { EventMessage, EventStream } from "../event/stream";
import type { BehaviourEventMessage, EventStreamWithStoredValue } from "../event/behaviour-stream";
import { useConstant } from "./use-constant";

/** A hook used to subscribe to any given event stream */
export const useEventStream = <T extends EventMessage>(eventStream: EventStream<T>["subject"], callback: (event: T) => void) => {
    useEffect(() => {
        const subscription = eventStream.subscribe(callback);
        return () => {
            subscription.unsubscribe();
        };
    }, []);
};

/** A hook used to subscribe to any given event stream containing a stored value.
 * On unmount, the event stream is reset to null
 * @returns The initial value of the event stream
 */
export const useEventStreamWithStoredValue = <D extends object, T extends BehaviourEventMessage<D>>(
    eventStream: EventStreamWithStoredValue<D, T>["subject"],
    callback: (event: ReturnType<EventStreamWithStoredValue<D, T>["subject"]["getValue"]>) => void
) => {
    const initialValue = useConstant(() => eventStream.getValue());
    useEffect(() => {
        const subscription = eventStream.subscribe(callback);
        return () => {
            subscription.unsubscribe();
            eventStream.next(null);
        };
    }, []);
    return initialValue;
};
